import axios from 'axios';

export default function ApiFactory(deps, options) {
  const { dataStore } = deps;
  const { apiHost, DT_OFFLINE = 1000 } = options;

  axios.defaults.baseURL = apiHost;

  const api = {
    // stores requests that were made during offline usage for retry later
    offlineQueue: [],

    // retries the messages in the specified queue
    async handleRetryQueue(queue) {
      if (queue.length === 0) {
        return true;
      }
      // get the oldest message in the queue
      const [{ resolve, reject, request }] = queue;
      let reply = null;
      let error = null;
      try {
        // try the request again
        const response = await axios({
          ...request,
          headers: {
            Authorization: `Bearer ${dataStore.get('token')}`,
          },
        });
        reply = response.data;
      } catch (err) {
        // if there isn't a response, stop processing the queue
        if (err.request && !err.response) {
          return false;
        }
        // this indicates that there was a problem with the API call to axios
        error = err;
      }
      // remove the first entry
      queue.shift();
      // return the output correctly
      if (error) {
        reject(error);
      }
      resolve(reply);
      // since we were successful, continue through the queue
      return this.handleRetryQueue(queue);
    },

    async request(req) {
      try {
        const res = await axios({
          ...req,
          headers: {
            Authorization: `Bearer ${dataStore.get('token')}`,
          },
        });
        return res.data;
      } catch (error) {
        // if there is a request but no response, the request was attempted
        // this indicates that the server did not reply and we will retry later
        // TODO: get more granular about what errors we retry and which we throw
        if (error.request && !error.response) {
          return new Promise((resolve, reject) => {
            this.offlineQueue.push({ request: req, resolve, reject });
          });
        }
        throw error;
      }
    },

    get(url) {
      return this.request({ method: 'get', url });
    },

    put(url, data) {
      return this.request({ method: 'put', url, data });
    },

    post(url, data) {
      return this.request({ method: 'post', url, data });
    },

    delete(url) {
      return this.request({ method: 'delete', url });
    },
  };

  // setup the offline queue retry timer and start it
  const offlineQueueTimer = async () => {
    await api.handleRetryQueue(api.offlineQueue);
    return setTimeout(offlineQueueTimer, DT_OFFLINE);
  };
  offlineQueueTimer();

  return api;
}
